/**
 * Advanced Simulation API Service
 *
 * API functions for the Advanced Simulation System.
 */

import apiClient, { rawApiClient } from './api';

// ============ Single Run Operations ============

/**
 * Start a single simulation run
 * @param {Object} params - Simulation parameters
 * @param {string} token - Auth token
 */
export const startAdvancedSimulationRun = async (params, token) => {
  try {
    const response = await apiClient.post('/advanced-simulation/run', params, token);
    return response.data;
  } catch (error) {
    console.error('Error starting simulation run:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get a single run by ID
 * @param {string} runId - Run ID
 * @param {string} token - Auth token
 */
export const getAdvancedSimulationRun = async (runId, token) => {
  try {
    const response = await apiClient.get(`/advanced-simulation/runs/${runId}`, token);
    return response.data;
  } catch (error) {
    console.error('Error getting simulation run:', error);
    throw error.response?.data || error;
  }
};

/**
 * List simulation runs
 * @param {Object} filters - Query filters
 * @param {string} token - Auth token
 */
export const listAdvancedSimulationRuns = async (filters = {}, token) => {
  try {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });

    const response = await apiClient.get(`/advanced-simulation/runs?${params.toString()}`, token);
    return response.data;
  } catch (error) {
    console.error('Error listing simulation runs:', error);
    throw error.response?.data || error;
  }
};

/**
 * Delete a run
 * @param {string} runId - Run ID
 * @param {string} token - Auth token
 */
export const deleteAdvancedSimulationRun = async (runId, token) => {
  try {
    const response = await apiClient.delete(`/advanced-simulation/runs/${runId}`, token);
    return response.data;
  } catch (error) {
    console.error('Error deleting run:', error);
    throw error.response?.data || error;
  }
};

// ============ Clone Operations ============

/**
 * Clone a simulation
 * @param {string} runId - Source run ID
 * @param {Object} options - Clone options
 * @param {string} token - Auth token
 */
export const cloneAdvancedSimulationRun = async (runId, options = {}, token) => {
  try {
    const response = await apiClient.post(`/advanced-simulation/runs/${runId}/clone`, options, token);
    return response.data;
  } catch (error) {
    console.error('Error cloning run:', error);
    throw error.response?.data || error;
  }
};

/**
 * Clone and re-run from a specific point
 * @param {string} runId - Source run ID
 * @param {number} turnIndex - Turn to edit
 * @param {string} editedContent - New content
 * @param {string} token - Auth token
 */
export const cloneAndRerunAdvancedSimulation = async (runId, turnIndex, editedContent, token) => {
  try {
    const response = await apiClient.post(
      `/advanced-simulation/runs/${runId}/clone-and-rerun`,
      { turnIndex, editedContent },
      token
    );
    return response.data;
  } catch (error) {
    console.error('Error cloning and re-running:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get clone lineage
 * @param {string} runId - Run ID
 * @param {string} token - Auth token
 */
export const getCloneLineage = async (runId, token) => {
  try {
    const response = await apiClient.get(`/advanced-simulation/runs/${runId}/lineage`, token);
    return response.data;
  } catch (error) {
    console.error('Error getting lineage:', error);
    throw error.response?.data || error;
  }
};

/**
 * Add human score to a turn
 * @param {string} runId - Run ID
 * @param {number} turnIndex - Turn index
 * @param {number} score - Score (1-10)
 * @param {string} notes - Optional notes
 * @param {string} token - Auth token
 */
export const scoreTurn = async (runId, turnIndex, score, notes, token) => {
  try {
    const response = await apiClient.post(
      `/advanced-simulation/runs/${runId}/score`,
      { turnIndex, score, notes },
      token
    );
    return response.data;
  } catch (error) {
    console.error('Error scoring turn:', error);
    throw error.response?.data || error;
  }
};

// ============ Batch Operations ============

/**
 * Start a batch of simulations
 * @param {Object} batchConfig - Batch configuration
 * @param {string} token - Auth token
 */
export const startAdvancedSimulationBatch = async (batchConfig, token) => {
  try {
    const response = await apiClient.post('/advanced-simulation/batch', batchConfig, token);
    return response.data;
  } catch (error) {
    console.error('Error starting batch:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get batch status
 * @param {string} batchId - Batch ID
 * @param {string} token - Auth token
 */
export const getAdvancedSimulationBatchStatus = async (batchId, token) => {
  try {
    const response = await apiClient.get(`/advanced-simulation/batch/${batchId}`, token);
    return response.data;
  } catch (error) {
    console.error('Error getting batch status:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get batch results with child runs
 * @param {string} batchId - Batch ID
 * @param {string} token - Auth token
 */
export const getAdvancedSimulationBatchResults = async (batchId, token) => {
  try {
    const response = await apiClient.get(`/advanced-simulation/batch/${batchId}/results`, token);
    return response.data;
  } catch (error) {
    console.error('Error getting batch results:', error);
    throw error.response?.data || error;
  }
};

/**
 * Cancel a running batch
 * @param {string} batchId - Batch ID
 * @param {string} token - Auth token
 */
/**
 * Re-run a completed batch with the same configuration
 * @param {string} batchId - Original batch ID to re-run
 * @param {string} token - Auth token
 * @param {string} [name] - Optional name override for the new batch
 */
export const rerunAdvancedSimulationBatch = async (batchId, token, name) => {
  try {
    const response = await apiClient.post(`/advanced-simulation/batch/${batchId}/rerun`, { name }, token);
    return response.data;
  } catch (error) {
    console.error('Error re-running batch:', error);
    throw error.response?.data || error;
  }
};

export const cancelAdvancedSimulationBatch = async (batchId, token) => {
  try {
    const response = await apiClient.post(`/advanced-simulation/batch/${batchId}/cancel`, {}, token);
    return response.data;
  } catch (error) {
    console.error('Error cancelling batch:', error);
    throw error.response?.data || error;
  }
};

/**
 * Delete a batch
 * @param {string} batchId - Batch ID
 * @param {string} token - Auth token
 */
export const deleteAdvancedSimulationBatch = async (batchId, token) => {
  try {
    const response = await apiClient.delete(`/advanced-simulation/batch/${batchId}`, token);
    return response.data;
  } catch (error) {
    console.error('Error deleting batch:', error);
    throw error.response?.data || error;
  }
};

/**
 * List batches
 * @param {Object} filters - Query filters
 * @param {string} token - Auth token
 */
export const listAdvancedSimulationBatches = async (filters = {}, token) => {
  try {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });

    const response = await apiClient.get(`/advanced-simulation/batch?${params.toString()}`, token);
    return response.data;
  } catch (error) {
    console.error('Error listing batches:', error);
    throw error.response?.data || error;
  }
};

// ============ Persona Operations ============

/**
 * Get available client personas
 * @param {string} token - Auth token
 */
export const getAdvancedSimulationClientPersonas = async (token) => {
  try {
    const response = await apiClient.get('/advanced-simulation/personas/client', token);
    return response.data;
  } catch (error) {
    console.error('Error getting client personas:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get available therapist personas
 * @param {string} token - Auth token
 */
export const getAdvancedSimulationTherapistPersonas = async (token) => {
  try {
    const response = await apiClient.get('/advanced-simulation/personas/therapist', token);
    return response.data;
  } catch (error) {
    console.error('Error getting therapist personas:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get personas by purpose
 * @param {string} purpose - Persona purpose (therapist, therapy_reviewer, onboarding_assistant, etc.)
 * @param {string} token - Auth token
 */
export const getPersonasByPurpose = async (purpose, token) => {
  try {
    const response = await apiClient.get(`/advanced-simulation/personas/by-purpose?purpose=${purpose}`, token);
    return response.data;
  } catch (error) {
    console.error('Error getting personas by purpose:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get available reviewer personas for RL Loop configuration
 * @param {string} token - Auth token
 */
export const getReviewerPersonas = async (token) => {
  try {
    const response = await apiClient.get('/advanced-simulation/reviewers', token);
    return response.data;
  } catch (error) {
    console.error('Error getting reviewer personas:', error);
    throw error.response?.data || error;
  }
};

// ============ Utility Operations ============

/**
 * Get cost configuration
 * @param {string} token - Auth token
 */
export const getCostConfig = async (token) => {
  try {
    const response = await apiClient.get('/advanced-simulation/cost-config', token);
    return response.data;
  } catch (error) {
    console.error('Error getting cost config:', error);
    throw error.response?.data || error;
  }
};

/**
 * Estimate batch cost
 * @param {Object} batchConfig - Batch configuration
 * @param {string} token - Auth token
 */
export const estimateBatchCost = async (batchConfig, token) => {
  try {
    const response = await apiClient.post('/advanced-simulation/estimate-cost', batchConfig, token);
    return response.data;
  } catch (error) {
    console.error('Error estimating cost:', error);
    throw error.response?.data || error;
  }
};

// ============ Summary & Export Operations ============

/**
 * Generate AI summary for a run
 * @param {string} runId - Run ID
 * @param {string} token - Auth token
 */
export const generateRunSummary = async (runId, token) => {
  try {
    const response = await apiClient.post(`/advanced-simulation/runs/${runId}/generate-summary`, {}, token);
    return response.data;
  } catch (error) {
    console.error('Error generating summary:', error);
    throw error.response?.data || error;
  }
};

/**
 * Generate AI review for a run
 * @param {string} runId - Run ID
 * @param {string} token - Auth token
 */
export const generateRunReview = async (runId, token, reviewerPersonaId = null) => {
  try {
    const body = reviewerPersonaId ? { reviewerPersonaId } : {};
    const response = await apiClient.post(`/advanced-simulation/runs/${runId}/generate-review`, body, token);
    return response.data;
  } catch (error) {
    console.error('Error generating review:', error);
    throw error.response?.data || error;
  }
};

/**
 * Generate client persona realism review for a completed run
 * @param {string} runId - Run ID
 * @param {string} token - Auth token
 */
export const generateClientRealismReview = async (runId, token) => {
  try {
    const response = await apiClient.post(`/advanced-simulation/runs/${runId}/generate-client-review`, {}, token);
    return response.data;
  } catch (error) {
    console.error('Error generating client realism review:', error);
    throw error.response?.data || error;
  }
};

/**
 * Export run as PDF
 * @param {string} runId - Run ID
 * @param {Object} options - Export options
 * @param {string} token - Auth token
 */
export const exportRunPDF = async (runId, options, token) => {
  try {
    const config = {
      headers: { 'x-auth-token': token },
      responseType: 'blob'
    };
    const response = await rawApiClient.post(
      `/advanced-simulation/runs/${runId}/export/pdf`,
      options,
      config
    );
    return response.data;
  } catch (error) {
    console.error('Error exporting PDF:', error);
    throw error.response?.data || error;
  }
};

/**
 * Export run as Markdown
 * @param {string} runId - Run ID
 * @param {string} token - Auth token
 */
export const exportRunMarkdown = async (runId, token) => {
  try {
    const config = {
      headers: { 'x-auth-token': token },
      responseType: 'text'
    };
    const response = await rawApiClient.get(
      `/advanced-simulation/runs/${runId}/export/markdown`,
      config
    );
    return response.data;
  } catch (error) {
    console.error('Error exporting Markdown:', error);
    throw error.response?.data || error;
  }
};

/**
 * Export run as JSON
 * @param {string} runId - Run ID
 * @param {string} token - Auth token
 */
export const exportRunJSON = async (runId, token) => {
  try {
    const config = {
      headers: { 'x-auth-token': token }
    };
    const response = await rawApiClient.get(`/advanced-simulation/runs/${runId}/export/json`, config);
    return response.data;
  } catch (error) {
    console.error('Error exporting JSON:', error);
    throw error.response?.data || error;
  }
};

/**
 * Export run as CSV
 * @param {string} runId - Run ID
 * @param {string} token - Auth token
 */
export const exportRunCSV = async (runId, token) => {
  try {
    const config = {
      headers: { 'x-auth-token': token },
      responseType: 'text'
    };
    const response = await rawApiClient.get(
      `/advanced-simulation/runs/${runId}/export/csv`,
      config
    );
    return response.data;
  } catch (error) {
    console.error('Error exporting CSV:', error);
    throw error.response?.data || error;
  }
};

// ============ A/B Testing Operations ============

/**
 * List A/B tests
 */
export const listABTests = async (filters = {}, token) => {
  try {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const response = await apiClient.get(`/advanced-simulation/ab-test?${params.toString()}`, token);
    return response.data;
  } catch (error) {
    console.error('Error listing A/B tests:', error);
    throw error.response?.data || error;
  }
};

/**
 * Create a new A/B test
 */
export const createABTest = async (testConfig, token) => {
  try {
    const response = await apiClient.post('/advanced-simulation/ab-test', testConfig, token);
    return response.data;
  } catch (error) {
    console.error('Error creating A/B test:', error);
    throw error.response?.data || error;
  }
};

/**
 * Update a draft A/B test
 */
export const updateABTest = async (testId, testConfig, token) => {
  try {
    const response = await apiClient.put(`/advanced-simulation/ab-test/${testId}`, testConfig, token);
    return response.data;
  } catch (error) {
    console.error('Error updating A/B test:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get a specific A/B test
 */
export const getABTest = async (testId, token) => {
  try {
    const response = await apiClient.get(`/advanced-simulation/ab-test/${testId}`, token);
    return response.data;
  } catch (error) {
    console.error('Error getting A/B test:', error);
    throw error.response?.data || error;
  }
};

/**
 * Start A/B test execution
 */
export const startABTest = async (testId, token) => {
  try {
    const response = await apiClient.post(`/advanced-simulation/ab-test/${testId}/start`, {}, token);
    return response.data;
  } catch (error) {
    console.error('Error starting A/B test:', error);
    throw error.response?.data || error;
  }
};

/**
 * Cancel an A/B test
 */
export const cancelABTest = async (testId, token) => {
  try {
    const response = await apiClient.post(`/advanced-simulation/ab-test/${testId}/cancel`, {}, token);
    return response.data;
  } catch (error) {
    console.error('Error cancelling A/B test:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get A/B test results with statistics
 */
export const getABTestResults = async (testId, token) => {
  try {
    const response = await apiClient.get(`/advanced-simulation/ab-test/${testId}/results`, token);
    return response.data;
  } catch (error) {
    console.error('Error getting A/B test results:', error);
    throw error.response?.data || error;
  }
};

/**
 * Conclude an A/B test (declare winner)
 */
export const concludeABTest = async (testId, conclusion, token) => {
  try {
    const response = await apiClient.post(`/advanced-simulation/ab-test/${testId}/conclude`, conclusion, token);
    return response.data;
  } catch (error) {
    console.error('Error concluding A/B test:', error);
    throw error.response?.data || error;
  }
};

/**
 * Add more runs to an A/B test for significance
 */
export const addABTestRuns = async (testId, additionalRunsPerVariant, token) => {
  try {
    const response = await apiClient.post(`/advanced-simulation/ab-test/${testId}/add-runs`, { additionalRunsPerVariant }, token);
    return response.data;
  } catch (error) {
    console.error('Error adding A/B test runs:', error);
    throw error.response?.data || error;
  }
};

/**
 * Export A/B test results
 */
export const exportABTestResults = async (testId, format = 'json', token) => {
  try {
    const response = await apiClient.get(`/advanced-simulation/ab-test/${testId}/export?format=${format}`, token);
    return response.data;
  } catch (error) {
    console.error('Error exporting A/B test results:', error);
    throw error.response?.data || error;
  }
};

/**
 * Clone an A/B test as a new draft
 */
export const cloneABTest = async (testId, overrides = {}, token) => {
  try {
    const response = await apiClient.post(`/advanced-simulation/ab-test/${testId}/clone`, overrides, token);
    return response.data;
  } catch (error) {
    console.error('Error cloning A/B test:', error);
    throw error.response?.data || error;
  }
};

/**
 * Delete an A/B test
 */
export const deleteABTest = async (testId, token) => {
  try {
    const response = await apiClient.delete(`/advanced-simulation/ab-test/${testId}`, token);
    return response.data;
  } catch (error) {
    console.error('Error deleting A/B test:', error);
    throw error.response?.data || error;
  }
};

// ============ Onboarding Simulation Operations ============

/**
 * Start an onboarding or full_journey simulation batch
 * @param {Object} params - Onboarding batch parameters
 * @param {string} token - Auth token
 */
export const startOnboardingBatch = async (params, token) => {
  try {
    const response = await apiClient.post('/advanced-simulation/onboarding-batch', params, token);
    return response.data;
  } catch (error) {
    console.error('Error starting onboarding batch:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get available onboarding flows for simulation
 * @param {string} token - Auth token
 */
export const getOnboardingFlows = async (token) => {
  try {
    const response = await apiClient.get('/advanced-simulation/onboarding/flows', token);
    return response.data;
  } catch (error) {
    console.error('Error getting onboarding flows:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get available triage strategies
 * @param {string} token - Auth token
 */
export const getTriageStrategies = async (token) => {
  try {
    const response = await apiClient.get('/advanced-simulation/onboarding/triage-strategies', token);
    return response.data;
  } catch (error) {
    console.error('Error getting triage strategies:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get client personas with onboarding profiles
 * @param {string} token - Auth token
 */
export const getOnboardingClientPersonas = async (token) => {
  try {
    const response = await apiClient.get('/advanced-simulation/onboarding/client-personas', token);
    return response.data;
  } catch (error) {
    console.error('Error getting onboarding client personas:', error);
    throw error.response?.data || error;
  }
};

// ============ Audio Export Operations ============

/**
 * Request audio export for a simulation run (Admin only)
 * @param {string} runId - Run ID
 * @param {Object} config - Export config { therapistVoiceId, clientVoiceId, includeSystemMessages }
 * @param {string} token - Auth token
 */
export const requestSimulationAudioExport = async (runId, config, token) => {
  try {
    const response = await apiClient.post(
      `/advanced-simulation/runs/${runId}/export-audio`,
      config,
      token
    );
    return response;
  } catch (error) {
    console.error('Error requesting simulation audio export:', error);
    throw error.response?.data || error;
  }
};

// ============ Persona Wizard Operations ============

/**
 * Start a persona wizard session
 * @param {string} instruction - Initial instruction for the wizard
 * @param {string} token - Auth token
 */
export const startPersonaWizard = async (instruction, token) => {
  try {
    const response = await apiClient.post('/advanced-simulation/persona-wizard/start', { instruction }, token);
    return response.data;
  } catch (error) {
    console.error('Error starting persona wizard:', error);
    throw error.response?.data || error;
  }
};

/**
 * Send a message to the persona wizard
 * @param {string} sessionId - Wizard session ID
 * @param {string} message - Message to send
 * @param {string} token - Auth token
 */
export const sendPersonaWizardMessage = async (sessionId, message, token) => {
  try {
    const response = await apiClient.post(`/advanced-simulation/persona-wizard/${sessionId}/message`, { message }, token);
    return response.data;
  } catch (error) {
    console.error('Error sending wizard message:', error);
    throw error.response?.data || error;
  }
};

/**
 * Apply generated personas from wizard session
 * @param {string} sessionId - Wizard session ID
 * @param {string} token - Auth token
 */
export const applyPersonaWizardPersonas = async (sessionId, token) => {
  try {
    const response = await apiClient.post(`/advanced-simulation/persona-wizard/${sessionId}/apply`, {}, token);
    return response.data;
  } catch (error) {
    console.error('Error applying wizard personas:', error);
    throw error.response?.data || error;
  }
};

export default {
  // Single run
  startAdvancedSimulationRun,
  getAdvancedSimulationRun,
  listAdvancedSimulationRuns,
  deleteAdvancedSimulationRun,
  // Clone
  cloneAdvancedSimulationRun,
  cloneAndRerunAdvancedSimulation,
  getCloneLineage,
  scoreTurn,
  // Batch
  startAdvancedSimulationBatch,
  rerunAdvancedSimulationBatch,
  getAdvancedSimulationBatchStatus,
  getAdvancedSimulationBatchResults,
  cancelAdvancedSimulationBatch,
  deleteAdvancedSimulationBatch,
  listAdvancedSimulationBatches,
  // Personas
  getAdvancedSimulationClientPersonas,
  getAdvancedSimulationTherapistPersonas,
  getPersonasByPurpose,
  getReviewerPersonas,
  // Utility
  getCostConfig,
  estimateBatchCost,
  // Summary & Export
  generateRunSummary,
  generateRunReview,
  exportRunPDF,
  exportRunMarkdown,
  exportRunJSON,
  exportRunCSV,
  // A/B Testing
  listABTests,
  createABTest,
  getABTest,
  startABTest,
  cancelABTest,
  getABTestResults,
  concludeABTest,
  addABTestRuns,
  exportABTestResults,
  cloneABTest,
  deleteABTest,
  // Onboarding Simulation
  startOnboardingBatch,
  getOnboardingFlows,
  getTriageStrategies,
  getOnboardingClientPersonas,
  // Audio Export
  requestSimulationAudioExport,
  // Client Realism Review
  generateClientRealismReview,
  // Persona Wizard
  startPersonaWizard,
  sendPersonaWizardMessage,
  applyPersonaWizardPersonas
};
